import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import { useIsomorphicLayoutEffect } from "react-use";

/** The team sections the phone tab bar shows; More lists everything else. */
export const TAB_SEGMENTS = ["chat", "overview", "movements"];

const TabBarContext = createContext<{
  hidden: boolean;
  setHiddenBy: React.Dispatch<React.SetStateAction<number>>;
  height: number;
  setHeight: (height: number) => void;
  appHeaderHidden: boolean;
  setAppHeaderHiddenBy: React.Dispatch<React.SetStateAction<number>>;
}>({
  hidden: false,
  setHiddenBy: () => {},
  height: 0,
  setHeight: () => {},
  appHeaderHidden: false,
  setAppHeaderHiddenBy: () => {},
});

const isTextField = (element: Element | null) =>
  element instanceof HTMLElement &&
  (element.isContentEditable ||
    element instanceof HTMLTextAreaElement ||
    (element instanceof HTMLInputElement &&
      !["checkbox", "radio", "button", "submit", "range", "file"].includes(
        element.type
      )));

// A fixed bottom bar rides above the software keyboard, so it steps aside
// while someone types on a touch screen. Hardware keyboards keep it.
const useTypingOnTouch = () => {
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const coarse = window.matchMedia("(pointer: coarse)");
    const update = () =>
      setTyping(coarse.matches && isTextField(document.activeElement));
    // activeElement is still the old field during focusout.
    const deferUpdate = () => setTimeout(update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", deferUpdate);
    return () => {
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", deferUpdate);
    };
  }, []);
  return typing;
};

// Screens that always push their own top bar. Before hydration no screen has
// said so yet, and the server's first paint must not show the shell's bars.
const PUSHED_ROUTES = [
  "/programs/[bootcampId]/workout/[workoutId]",
  "/blocks/[blockId]",
];

export const TabBarProvider = ({ children }: { children: React.ReactNode }) => {
  const [hiddenBy, setHiddenBy] = useState(0);
  const [height, setHeight] = useState(0);
  const [appHeaderHiddenBy, setAppHeaderHiddenBy] = useState(0);
  const typing = useTypingOnTouch();
  const { pathname } = useRouter();
  const [hydrated, setHydrated] = useState(false);
  useIsomorphicLayoutEffect(() => setHydrated(true), []);
  const pushedRoute = !hydrated && PUSHED_ROUTES.includes(pathname);
  return (
    <TabBarContext.Provider
      value={{
        hidden: hiddenBy > 0 || typing || pushedRoute,
        setHiddenBy,
        height,
        setHeight,
        appHeaderHidden: appHeaderHiddenBy > 0 || pushedRoute,
        setAppHeaderHiddenBy,
      }}
    >
      {children}
    </TabBarContext.Provider>
  );
};

/**
 * Hides the tab bar while `hidden` is true, for screens that own the bottom
 * of the screen (a chat composer, a mode's action bar).
 */
export const useHideTabBar = (hidden = true) => {
  const { setHiddenBy } = useContext(TabBarContext);
  // Before paint, so the screen never shows a frame sized for the wrong bar.
  useIsomorphicLayoutEffect(() => {
    if (!hidden) return;
    setHiddenBy((count) => count + 1);
    return () => setHiddenBy((count) => count - 1);
  }, [hidden, setHiddenBy]);
};

export const useTabBarHidden = () => useContext(TabBarContext).hidden;

/**
 * Hides the phone's app header (logo and team switcher) while `hidden` is
 * true, for a pushed screen that draws its own top bar. No effect at lg+.
 */
export const useHideAppHeader = (hidden = true) => {
  const { setAppHeaderHiddenBy } = useContext(TabBarContext);
  useIsomorphicLayoutEffect(() => {
    if (!hidden) return;
    setAppHeaderHiddenBy((count) => count + 1);
    return () => setAppHeaderHiddenBy((count) => count - 1);
  }, [hidden, setAppHeaderHiddenBy]);
};

export const useAppHeaderHidden = () =>
  useContext(TabBarContext).appHeaderHidden;

/**
 * Measures --tab-bar-height in pixels for code that can't use the CSS
 * variable (virtualized list heights). Render it inside the element that
 * defines the variable.
 */
export const TabBarHeightProbe = () => {
  const { setHeight } = useContext(TabBarContext);
  const ref = useRef<HTMLDivElement>(null);
  useIsomorphicLayoutEffect(() => {
    const probe = ref.current;
    if (!probe) return;
    const observer = new ResizeObserver(() => setHeight(probe.offsetHeight));
    observer.observe(probe);
    return () => observer.disconnect();
  }, [setHeight]);
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none invisible fixed bottom-0 left-0 h-[var(--tab-bar-height)] w-0"
    />
  );
};

/** The tab bar's current height in pixels: 0 at lg+ or while it's hidden. */
export const useTabBarHeight = () => useContext(TabBarContext).height;
